import { useRef, useState } from "react";
import type { ListingItem, ListingSection } from "../../content/industry";
import { Photo } from "./Photo";
import { SectionHeading } from "./SectionHeading";

/**
 * Fichas con cinco composiciones (habitaciones, destinos, propiedades, proyectos y productos). Si el contenido define
 * «categories», aparece un filtro accesible; si una ficha tiene «details», el botón abre su ficha completa en un diálogo
 * nativo (se cierra con Esc). Los datos son de ejemplo: no hay base de datos detrás.
 */
export function Listing({ section }: { section: ListingSection }) {
  const all = "Todas";
  const [filter, setFilter] = useState(all);
  const [open, setOpen] = useState<ListingItem | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const items = filter === all ? section.items : section.items.filter(item => item.category === filter);
  const show = (item: ListingItem) => { setOpen(item); dialog.current?.showModal(); };
  return (
    <section id={section.id} className={`ix-section ix-listing ix-listing--${section.variant}`} aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        {section.categories?.length ? (
          <div className="ix-tabs ix-filter" role="group" aria-label={section.filterLabel ?? "Filtrar"}>
            {[all, ...section.categories].map(category => <button key={category} type="button" aria-pressed={filter === category} onClick={() => setFilter(category)}>{category}</button>)}
          </div>
        ) : null}
        <p className="ix-sr-only" aria-live="polite">{items.length} resultados</p>
        <ul className="ix-cards">
          {items.map(item => <li key={item.title} className="ix-card">
            <div className="ix-card-media"><Photo index={item.image} sizes="(min-width: 1024px) 33vw, 100vw" />{item.badge ? <span className="ix-badge">{item.badge}</span> : null}</div>
            <div className="ix-card-body">
              {item.location ? <p className="ix-card-location">{item.location}</p> : null}
              <h3>{item.title}</h3>
              <p className="ix-card-text">{item.text}</p>
              {item.meta.length ? <ul className="ix-meta">{item.meta.map(meta => <li key={meta}>{meta}</li>)}</ul> : null}
              <div className="ix-card-footer">
                {item.price ? <p className="ix-card-price"><strong>{item.price}</strong>{item.priceNote ? <span> {item.priceNote}</span> : null}</p> : <span />}
                <span className="ix-card-actions">
                  {item.link?.href ? <a className="ix-link-button" href={item.link.href} target="_blank" rel="noopener noreferrer">{item.link.label}<span className="ix-sr-only"> de {item.title} (abre en otra pestaña)</span></a> : null}
                  {item.details ? <button type="button" className="ix-link-button" onClick={() => show(item)}>Ver ficha<span className="ix-sr-only"> de {item.title}</span></button> : null}
                </span>
              </div>
            </div>
          </li>)}
        </ul>
        {items.length === 0 ? <p className="ix-note">No hay resultados con este filtro.</p> : null}
        {section.note ? <p className="ix-note">{section.note}</p> : null}
        {section.cta ? <a className="ix-button ix-button--ghost ix-listing-cta" href={section.cta.href}>{section.cta.label}</a> : null}
      </div>
      <dialog ref={dialog} className="ix-dialog" aria-labelledby={`${section.id}-dialog-title`} onClose={() => setOpen(null)}>
        {open ? (
          <div className="ix-dialog-body">
            <Photo index={open.image} className="ix-dialog-photo" sizes="(min-width: 768px) 640px, 100vw" />
            <div className="ix-dialog-content">
              {open.location ? <p className="ix-card-location">{open.location}</p> : null}
              <h3 id={`${section.id}-dialog-title`}>{open.title}</h3>
              {open.price ? <p className="ix-card-price"><strong>{open.price}</strong>{open.priceNote ? <span> {open.priceNote}</span> : null}</p> : null}
              <p>{open.details?.description}</p>
              <ul className="ix-specs">{open.details?.features.map(feature => <li key={feature}>{feature}</li>)}</ul>
              <div className="ix-actions">
                {section.cta ? <a className="ix-button ix-button--primary" href={section.cta.href} onClick={() => dialog.current?.close()}>{section.cta.label}</a> : null}
                <button type="button" className="ix-button ix-button--ghost" onClick={() => dialog.current?.close()}>Cerrar</button>
              </div>
            </div>
          </div>
        ) : null}
      </dialog>
    </section>
  );
}
